/**
 * A tiny event emitter that mirrors the Vue 2 instance event API
 * (`$on` / `$off` / `$once` / `$emit`).
 *
 * Vue 3 removed `$on`, `$off` and `$once` from component instances, which broke
 * the old `Vue.prototype.$hub = new Vue()` event-bus pattern. This class is a
 * drop-in replacement exposing the exact same `$`-prefixed methods (including
 * multi-argument `$emit`), so every existing `this.$hub.$on(...)` /
 * `this.$hub.$emit(...)` call keeps working unchanged.
 */
export default class EventBus {
    constructor() {
        this.handlers = Object.create(null);
    }

    $on(event, handler) {
        (this.handlers[event] || (this.handlers[event] = [])).push(handler);
        return this;
    }

    $off(event, handler) {
        if (!event) {
            this.handlers = Object.create(null);
            return this;
        }
        const list = this.handlers[event];
        if (!list) return this;
        if (!handler) {
            delete this.handlers[event];
            return this;
        }
        this.handlers[event] = list.filter((h) => h !== handler && h.fn !== handler);
        return this;
    }

    $once(event, handler) {
        const wrapper = (...args) => {
            this.$off(event, wrapper);
            handler.apply(this, args);
        };
        wrapper.fn = handler;
        return this.$on(event, wrapper);
    }

    $emit(event, ...args) {
        const list = this.handlers[event];
        if (!list) return this;
        list.slice().forEach((handler) => handler.apply(this, args));
        return this;
    }
}
